import { Box, Chip, Divider, Link, Typography, useTheme } from '@mui/material'
// biome-ignore lint/style/useImportType: <This is not a type-only import>
import React from 'react'
import { useErrorHandler } from 'react-error-boundary'

// Filter selections from URL query parameters (querystring.parse result)
interface FilterSelections {
  categories?: string | string[]
  techstarsCompany?: string | string[]
  partner?: string | string[]
  text?: string | string[]
  sort?: string | string[]
  page?: string | string[]
  [key: string]: string | string[] | undefined
}

// Handler value types matching PerksListing usage
type FilterValue = string | string[] | boolean | number

interface PerksFiltersProps {
  filterSelections?: FilterSelections
  availableCategories?: string[]
  onChangeHandler?: (
    name: string,
    value: FilterValue,
    allowMultiple: boolean,
  ) => void
  clearFiltersHandler?: () => void
}

// Perk Highlights data structure
const perkHighlights = [
  {
    key: 'techstarsCompany',
    label: 'Techstars Company',
    value: 'Techstars Company',
  },
  {
    key: 'partner',
    label: 'Network Partner Perks',
    value: 'Network Partner Perks',
  },
]

const PerksFilters: React.FC<PerksFiltersProps> = ({
  filterSelections = {},
  availableCategories = [],
  onChangeHandler,
  clearFiltersHandler,
}) => {
  const theme = useTheme()
  const handleError = useErrorHandler()

  const handleFilterChange = (
    filterName: string,
    value: string,
    selected: boolean,
  ) => {
    if (onChangeHandler) {
      try {
        // Handle multiple values for categories
        if (filterName === 'categories') {
          const currentSelection = filterSelections[filterName]
          const currentValues: string[] = Array.isArray(currentSelection)
            ? currentSelection
            : currentSelection
              ? [currentSelection]
              : []

          let newValues: string[]
          if (selected) {
            newValues = [...currentValues, value]
          } else {
            newValues = currentValues.filter((v: string) => v !== value)
          }

          onChangeHandler(filterName, newValues, true)
        } else {
          // Handle single values for other filters
          onChangeHandler(filterName, selected ? value : '', false)
        }
      } catch (err) {
        // Trigger error boundary for filter handler failures
        handleError(
          err instanceof Error
            ? err
            : new Error('Failed to update filter selection'),
        )
      }
    }
  }

  const isSelected = (filterName: string, value: string) => {
    if (filterName === 'categories') {
      const categories = filterSelections[filterName]
      return Array.isArray(categories) && categories.includes(value)
    }
    return filterSelections[filterName] === value
  }

  const hasActiveFilters = () => {
    // Check if any filters are selected (excluding 'page' and 'sort' as they're not user filters)
    const filterKeys = Object.keys(filterSelections).filter(
      (key) => key !== 'page' && key !== 'sort',
    )

    return filterKeys.some((key) => {
      const value = filterSelections[key]
      if (Array.isArray(value)) {
        return value.length > 0
      }
      return value && value !== ''
    })
  }

  return (
    <Box
      sx={{
        bgcolor: 'background.paper',
        borderRadius: 2,
        border: `1px solid ${theme.palette.divider}`,
        p: 3,
      }}
    >
      {/* Filter Header */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 2,
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          Filter By
        </Typography>
        {hasActiveFilters() && (
          <Link
            component="button"
            variant="body2"
            onClick={clearFiltersHandler}
            sx={{
              textDecoration: 'none',
              '&:hover': { textDecoration: 'underline' },
            }}
          >
            Clear All
          </Link>
        )}
      </Box>

      {/* Perk Highlights Section */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle1" sx={{ mb: 2 }}>
          Perk Highlights
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {/* Perk Highlights Section */}
          {perkHighlights.map((highlight) => (
            <Chip
              key={highlight.key}
              label={highlight.label}
              variant={
                isSelected(highlight.key, highlight.value)
                  ? 'filled'
                  : 'outlined'
              }
              color={
                isSelected(highlight.key, highlight.value)
                  ? 'primary'
                  : 'default'
              }
              size="small"
              clickable
              onClick={() => {
                handleFilterChange(
                  highlight.key,
                  highlight.value,
                  !isSelected(highlight.key, highlight.value),
                )
              }}
            />
          ))}
        </Box>
      </Box>

      <Divider sx={{ my: 2 }} />

      {/* Categories Section */}
      <Box>
        <Typography variant="subtitle1" sx={{ mb: 2 }}>
          Categories
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {availableCategories.map((category) => (
            <Chip
              key={category}
              label={category}
              variant={
                isSelected('categories', category) ? 'filled' : 'outlined'
              }
              color={isSelected('categories', category) ? 'primary' : 'default'}
              size="small"
              clickable
              onClick={() =>
                handleFilterChange(
                  'categories',
                  category,
                  !isSelected('categories', category),
                )
              }
            />
          ))}
        </Box>
        {availableCategories.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            Loading categories...
          </Typography>
        )}
      </Box>
    </Box>
  )
}

export default PerksFilters
